import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { roadmapsGuard } from './core/guards/roadmaps.guard';
import { systemAdminGuard } from './core/guards/system-admin.guard';
import { workspaceAdminGuard } from './core/guards/workspace-admin.guard';
import { operatorPermissionGuard } from './core/guards/operator-permission.guard';
import { accountGateGuard } from './core/guards/account-gate.guard';
import { workspaceAccessGuard } from './core/guards/workspace-access.guard';
import { createProjectGuard } from './core/guards/create-project.guard';
import { deleteProjectGuard } from './core/guards/delete-project.guard';
import { insightsGuard } from './core/guards/insights.guard';
import { projectAdminGuard } from './core/guards/project-admin.guard';
import { landingGuard } from './core/guards/landing.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [landingGuard],
    loadComponent: () => import('./features/marketing/landing.component').then(m => m.LandingComponent),
  },
  { path: 'login', loadComponent: () => import('./features/auth/login.component').then(m => m.LoginComponent) },
  { path: 'super-admin/login', loadComponent: () => import('./features/auth/super-admin-login.component').then(m => m.SuperAdminLoginComponent) },
  { path: 'signup', loadComponent: () => import('./features/auth/sign-up.component').then(m => m.SignUpComponent) },
  { path: 'verify-email', loadComponent: () => import('./features/auth/verify-email.component').then(m => m.VerifyEmailComponent) },
  { path: 'forgot-password', loadComponent: () => import('./features/auth/forgot-password.component').then(m => m.ForgotPasswordComponent) },
  { path: 'reset-password', loadComponent: () => import('./features/auth/reset-password.component').then(m => m.ResetPasswordComponent) },

  // Public legal pages (each has its own link).
  { path: 'privacy-policy', loadComponent: () => import('./features/legal/privacy-policy.component').then(m => m.PrivacyPolicyComponent) },
  { path: 'privacy', redirectTo: 'privacy-policy', pathMatch: 'full' },
  { path: 'terms', loadComponent: () => import('./features/legal/terms.component').then(m => m.TermsComponent) },
  { path: 'terms-and-conditions', redirectTo: 'terms', pathMatch: 'full' },
  { path: 'refund-policy', loadComponent: () => import('./features/legal/refund-policy.component').then(m => m.RefundPolicyComponent) },
  { path: 'return-policy', redirectTo: 'refund-policy', pathMatch: 'full' },
  { path: 'change-password', canActivate: [authGuard], loadComponent: () => import('./features/auth/change-password.component').then(m => m.ChangePasswordComponent) },
  {
    path: 'workspaces',
    canActivate: [authGuard],
    loadComponent: () => import('./features/auth/workspace-picker.component').then(m => m.WorkspacePickerComponent),
  },
  {
    path: '',
    loadComponent: () => import('./layout/main-layout.component').then(m => m.MainLayoutComponent),
    canActivate: [authGuard, accountGateGuard, workspaceAccessGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'profile', loadComponent: () => import('./features/profile/profile.component').then(m => m.ProfileComponent) },
      { path: 'about', loadComponent: () => import('./features/profile/about-wiztasks.component').then(m => m.AboutWiztasksComponent) },
      { path: 'dashboard', loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent) },
      { path: 'insights', canActivate: [insightsGuard], loadComponent: () => import('./features/insights/insights-hub.component').then(m => m.InsightsHubComponent) },
      {
        path: 'insights/:dashboardId',
        canActivate: [insightsGuard],
        loadComponent: () => import('./features/insights/insights-dashboard.component').then(m => m.InsightsDashboardComponent),
      },
      { path: 'issues', loadComponent: () => import('./features/issue/all-issues.component').then(m => m.AllIssuesComponent) },
      { path: 'projects', loadComponent: () => import('./features/projects/project-list.component').then(m => m.ProjectListComponent) },
      {
        path: 'projects/trash',
        canActivate: [deleteProjectGuard],
        loadComponent: () => import('./features/projects/project-trash.component').then(m => m.ProjectTrashComponent),
      },
      {
        path: 'projects/create',
        canActivate: [createProjectGuard],
        loadComponent: () => import('./features/projects/project-form.component').then(m => m.ProjectFormComponent),
      },
      {
        path: 'projects/:projectSlug',
        loadComponent: () => import('./features/project-detail/project-detail.component').then(m => m.ProjectDetailComponent),
        children: [
          { path: '', redirectTo: 'summary', pathMatch: 'full' },
          {
            path: 'summary',
            loadComponent: () => import('./features/project-summary/project-summary.component').then((m) => m.ProjectSummaryComponent),
          },
          {
            path: 'work',
            loadComponent: () => import('./features/issue/all-issues.component').then((m) => m.AllIssuesComponent),
          },
          {
            path: 'timeline',
            canActivate: [roadmapsGuard],
            loadComponent: () => import('./features/project-timeline/project-timeline.component').then((m) => m.ProjectTimelineComponent),
          },
          {
            path: 'calendar',
            loadComponent: () => import('./features/project-calendar/project-calendar.component').then((m) => m.ProjectCalendarComponent),
          },
          {
            path: 'reports',
            loadComponent: () => import('./features/project-reports/project-reports.component').then((m) => m.ProjectReportsComponent),
          },
          { path: 'board', loadComponent: () => import('./features/board/board.component').then(m => m.BoardComponent) },
          { path: 'backlog', loadComponent: () => import('./features/backlog/backlog.component').then(m => m.BacklogComponent) },
          {
            path: 'settings',
            canActivate: [projectAdminGuard],
            loadComponent: () => import('./features/project-settings/project-settings.component').then(m => m.ProjectSettingsComponent),
          },
          {
            path: 'settings/board',
            loadComponent: () => import('./features/project-settings/team-board-settings.component').then(m => m.TeamBoardSettingsComponent),
          },
          { path: 'workflows/:id/edit', loadComponent: () => import('./features/admin/workflow-editor.component').then(m => m.WorkflowEditorComponent) },
          {
            path: 'workflows/:id/canvas',
            loadComponent: () =>
              import('./features/admin/workflow-graph-canvas.component').then((m) => m.WorkflowGraphCanvasComponent),
          },
        ],
      },
      { path: 'issue/:id', loadComponent: () => import('./features/issue/issue-detail.component').then(m => m.IssueDetailComponent) },
      { path: 'billing', loadComponent: () => import('./features/billing/billing.component').then(m => m.BillingComponent) },
    ],
  },
  {
    // WizTasks Operator Console — the cross-workspace platform control plane.
    // Separate shell from the workspace `/admin` area; operator (is_super_admin) only.
    path: 'operator',
    loadComponent: () => import('./layout/operator-layout.component').then(m => m.OperatorLayoutComponent),
    canActivate: [authGuard, systemAdminGuard, accountGateGuard],
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      { path: 'overview', loadComponent: () => import('./features/admin/global-dashboard.component').then(m => m.GlobalDashboardComponent) },
      { path: 'account', loadComponent: () => import('./features/operator/operator-account.component').then(m => m.OperatorAccountComponent) },
      {
        path: 'companies',
        data: { audience: 'company' },
        loadComponent: () => import('./features/admin/subscriptions/tenants-admin.component').then(m => m.TenantsAdminComponent),
      },
      {
        path: 'individuals',
        data: { audience: 'individual' },
        loadComponent: () => import('./features/admin/subscriptions/tenants-admin.component').then(m => m.TenantsAdminComponent),
      },
      { path: 'tenant/:id', loadComponent: () => import('./features/operator/tenant-insight.component').then(m => m.TenantInsightComponent) },
      {
        path: 'plans',
        canActivate: [operatorPermissionGuard('plans.manage')],
        loadComponent: () => import('./features/admin/subscriptions/plans-admin.component').then(m => m.PlansAdminComponent),
      },
      {
        path: 'operators',
        canActivate: [operatorPermissionGuard('operators.manage')],
        loadComponent: () => import('./features/operator/operators-admin.component').then(m => m.OperatorsAdminComponent),
      },
      { path: 'waitlist', loadComponent: () => import('./features/operator/waitlist-admin.component').then(m => m.WaitlistAdminComponent) },
    ],
  },
  // Back-compat: old operator links lived under /admin. Keep them working.
  { path: 'admin/dashboard', redirectTo: 'operator/overview', pathMatch: 'full' },
  { path: 'admin/subscriptions', redirectTo: 'operator/companies', pathMatch: 'full' },
  { path: 'admin/plans', redirectTo: 'operator/plans', pathMatch: 'full' },
  {
    path: 'admin',
    loadComponent: () => import('./layout/admin-layout.component').then(m => m.AdminLayoutComponent),
    canActivate: [authGuard, workspaceAdminGuard, accountGateGuard],
    children: [
      { path: '', redirectTo: 'members', pathMatch: 'full' },
      { path: 'members', loadComponent: () => import('./features/admin/members/members.component').then(m => m.MembersComponent) },
      { path: 'user-management', redirectTo: 'members', pathMatch: 'full' },
      { path: 'groups/create', loadComponent: () => import('./features/admin/group-create.component').then(m => m.GroupCreateComponent) },
      { path: 'groups/:id', loadComponent: () => import('./features/admin/group-detail.component').then(m => m.GroupDetailComponent) },
      { path: 'permission-schemes', loadComponent: () => import('./features/admin/permission-schemes-list.component').then(m => m.PermissionSchemesListComponent) },
      { path: 'permission-schemes/:id', loadComponent: () => import('./features/admin/permission-scheme-editor.component').then(m => m.PermissionSchemeEditorComponent) },
      { path: 'workflow-schemes', loadComponent: () => import('./features/admin/workflow-schemes-list.component').then(m => m.WorkflowSchemesListComponent) },
      { path: 'workflow-schemes/:id', loadComponent: () => import('./features/admin/workflow-scheme-editor.component').then(m => m.WorkflowSchemeEditorComponent) },
      { path: 'workflows', loadComponent: () => import('./features/admin/workflows-admin.component').then(m => m.WorkflowsAdminComponent) },
      { path: 'workflows/create', loadComponent: () => import('./features/admin/workflow-create.component').then(m => m.WorkflowCreateComponent) },
      { path: 'workflows/:id/edit', loadComponent: () => import('./features/admin/workflow-editor.component').then(m => m.WorkflowEditorComponent) },
      {
        path: 'workflows/:id/canvas',
        loadComponent: () =>
          import('./features/admin/workflow-graph-canvas.component').then((m) => m.WorkflowGraphCanvasComponent),
      },
      { path: 'issues', loadComponent: () => import('./features/admin/issues-admin.component').then(m => m.IssuesAdminComponent) },
      { path: 'issue-screens', loadComponent: () => import('./features/admin/issue-screens-admin.component').then(m => m.IssueScreensAdminComponent) },
      { path: 'issue-screens/:id/edit', loadComponent: () => import('./features/admin/issue-screens-admin.component').then(m => m.IssueScreensAdminComponent) },
      { path: 'issue-screen-schemes', loadComponent: () => import('./features/admin/issue-screen-schemes-admin.component').then(m => m.IssueScreenSchemesAdminComponent) },
      { path: 'issue-screen-schemes/:id/edit', loadComponent: () => import('./features/admin/issue-screen-schemes-admin.component').then(m => m.IssueScreenSchemesAdminComponent) },
      { path: 'issue-type-screen-schemes', loadComponent: () => import('./features/admin/issue-type-screen-schemes-admin.component').then(m => m.IssueTypeScreenSchemesAdminComponent) },
      { path: 'issue-type-screen-schemes/:id/edit', loadComponent: () => import('./features/admin/issue-type-screen-schemes-admin.component').then(m => m.IssueTypeScreenSchemesAdminComponent) },
      { path: 'notification-schemes', loadComponent: () => import('./features/admin/notification-schemes-admin.component').then(m => m.NotificationSchemesAdminComponent) },
      { path: 'boards', loadComponent: () => import('./features/admin/boards-admin.component').then(m => m.BoardsAdminComponent) },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
